import { Box, Button, Container, Tab, Tabs, Typography } from '@mui/material'
import { ReactElement } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppSelector } from 'src/store/hooks'
import {
  selectBoundTreeStatus,
  selectUseIBSCV2,
  selectUseRecertificationCycle,
} from 'src/v3/settings/settings.slice'

type Props = {
  children: any
}

export default (props: Props): ReactElement => {
  let location = useLocation()
  let navigate = useNavigate()

  let boundTreeStatus = useAppSelector(selectBoundTreeStatus)
  let useRecertificationCycle = useAppSelector(selectUseRecertificationCycle)
  let useIBSCV2 = useAppSelector(selectUseIBSCV2)

  let activeTab =
    location.pathname === '/profile'
      ? ''
      : location.pathname
          .replace('/profile/', '')
          .replace(/recertification\/.*/, 'recertification')

  let tabMeta: Record<string, { panelId: string; tabId: string }> = {
    '': { panelId: 'tabpanel-details', tabId: 'account-details' },
    preferences: { panelId: 'tabpanel-preferences', tabId: 'user-preferences' },
    certificates: {
      panelId: 'tabpanel-certificates',
      tabId: 'user-certificates',
    },
    licenses: {
      panelId: 'tabpanel-licenses-and-certifications',
      tabId: 'user-licenses-and-certifications',
    },
    credentials: {
      panelId: 'tabpanel-credentials',
      tabId: 'user-credentials',
    },
    ibsc: { panelId: 'tabpanel-ibsc', tabId: 'ibsc-progress' },
    recertification: {
      panelId: 'tabpanel-recertification',
      tabId: 'recertification-cycles',
    },
    tours: { panelId: 'tabpanel-tours', tabId: 'profile-tours' },
  }
  let hasAccess = boundTreeStatus !== 'limited'

  let visibleTabs = new Set([
    '',
    'certificates',
    'licenses',
    'tours',
    ...(hasAccess ? ['preferences', 'credentials'] : []),
    ...(hasAccess && useIBSCV2 ? ['ibsc'] : []),
    ...(hasAccess && useRecertificationCycle ? ['recertification'] : []),
  ])

  let activeMeta = tabMeta[activeTab] ?? tabMeta['']
  let activePanelId = activeMeta.panelId
  let activeTabId = visibleTabs.has(activeTab) ? activeMeta.tabId : undefined

  return (
    <Container maxWidth="xl" sx={{ p: 3 }}>
      <a
        href="#main-content"
        style={{
          position: 'absolute',
          left: '-9999px',
          zIndex: 999,
        }}
        onFocus={(e) => {
          // eslint-disable-next-line fp/no-mutation
          e.currentTarget.style.left = '0'
        }}
        onBlur={(e) => {
          // eslint-disable-next-line fp/no-mutation
          e.currentTarget.style.left = '-9999px'
        }}
      >
        Skip to main content
      </a>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          className="mb-6"
          id="profile-page-heading"
        >
          User Profile
        </Typography>
        {!activeTab && hasAccess && (
          <Button
            color="secondary"
            variant="outlined"
            href="https://billing.stripe.com/p/login/dR600G9C1ePj6MEeUU"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Manage your subscription on Stripe (opens in new window)"
          >
            Manage Subscription
          </Button>
        )}
      </Box>
      <Tabs
        value={activeTab}
        onChange={(_: React.SyntheticEvent, newTab: string) => {
          navigate(`/profile/${newTab}`)
        }}
        variant="scrollable"
        scrollButtons
        allowScrollButtonsMobile
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        aria-label="User profile navigation"
      >
        <Tab
          label="Account Details"
          value=""
          id="account-details"
          aria-controls="tabpanel-details"
          data-tour="profile-account-details-tab"
        />
        {hasAccess && (
          <Tab
            label="Preferences"
            value="preferences"
            id="user-preferences"
            aria-controls="tabpanel-preferences"
            data-tour="profile-preferences-tab"
          />
        )}
        <Tab
          label="Certificates"
          value="certificates"
          id="user-certificates"
          aria-controls="tabpanel-certificates"
        />
        <Tab
          label="Licenses & Certifications"
          value="licenses"
          id="user-licenses-and-certifications"
          aria-controls="tabpanel-licenses-and-certifications"
          data-tour="profile-licenses-tab"
        />
        {hasAccess && (
          <Tab
            label="Credentials"
            value="credentials"
            id="user-credentials"
            aria-controls="tabpanel-credentials"
          />
        )}
        {hasAccess && useIBSCV2 && (
          <Tab
            label="IBSC"
            value="ibsc"
            id="ibsc-progress"
            aria-controls="tabpanel-ibsc"
          />
        )}
        {hasAccess && useRecertificationCycle && (
          <Tab
            label="Recertification"
            value="recertification"
            id="recertification-cycles"
            aria-controls="tabpanel-recertification"
          />
        )}
        <Tab
          label="Tours"
          value="tours"
          id="profile-tours"
          aria-controls="tabpanel-tours"
          data-tour="profile-tours-tab"
        />
      </Tabs>
      <Box id="main-content">
        <Box role="tabpanel" id={activePanelId} aria-labelledby={activeTabId}>
          {props.children}
        </Box>
      </Box>
    </Container>
  )
}
